import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Container,
  Grid,
  Skeleton,
  Stack,
  Typography,
  LinearProgress,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";

import BoxContainer from "common/BoxContainer/BoxContainer";
import { useSnackbar } from "notistack";
import { useApp } from "contexts/AppContext";
import axios from "axios-config";
import SettingsHeader from "./SettingsHeader";
import { fetchBilling } from "api/organizations";
import { useQuery } from "react-query";
import dayjs from "dayjs";
import { is } from "react-date-range/dist/locale";
import { LoyaltyOutlined, ManageAccounts } from "@mui/icons-material";

const Billing = () => {
  const { t } = useTranslation();

  const {
    data: billingData,
    isLoading,
    isError,
  } = useQuery("billingData", fetchBilling, {
    refetchOnWindowFocus: false,
    // refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
  });

  console.log("billingData", billingData);

  const pageContent = () => {
    if (isLoading) {
      return (
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="text" width="40%" />
              <Skeleton variant="rectangular" height={118} />
            </Stack>
          </CardContent>
        </Card>
      );
    }
    if (isError) {
      return (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h4" color="error">
              {t("settings.billing.error")}
            </Typography>
          </CardContent>
        </Card>
      );
    }
    const productItems = billingData?.paddleProducts?.[0]?.items ?? [];
    const firstItem = productItems?.[0];
    const subscriptionType =
      billingData?.organizationSubscription?.subscriptionType;
    // const nextBilledAt = billingData?.paddleProducts?.[0]?.nextBilledAt;
    const nextBilledAt = billingData?.paddleProducts?.[0]?.nextBilledAt;
    const subscriptionEndsAt = billingData?.subscription?.endsAt;
    const paddleCustomerPortalUrl =
      billingData?.paddleCustomerPortal?.urls?.general?.overview;
    let currentBillingPeriod =
      billingData?.paddleProducts?.[0]?.currentBillingPeriod;
    if (!currentBillingPeriod) {
      currentBillingPeriod = {
        startsAt: billingData?.subscription?.startDate,
        endsAt: billingData?.subscription?.endDate,
      };
    }

    return (
      <>
        <Card
          variant="outlined"
          sx={{
            p: 2, // increased padding
            borderRadius: 3, // more rounded corners
            boxShadow: "0 4px 16px rgba(0,0,0,0.1)", // elevated shadow
            backgroundColor: "#fff",
          }}
        >
          <CardContent>
            <Typography variant="h4" gutterBottom>
              {t("settings.billing.currentPlan")}
            </Typography>
            {firstItem ? (
              <Typography variant="body1" color="textSecondary">
                {firstItem.price.name} {firstItem.product.name}
              </Typography>
            ) : billingData?.subscription?.status === "trialing" ? (
              <Typography variant="body1" color="textSecondary">
                {t("settings.billing.trialInformation")}
              </Typography>
            ) : (
              <Typography variant="body1" color="textSecondary">
                {t("settings.billing.noPlan")}
              </Typography>
            )}
            {billingData?.subscription?.planDetails && (
              <Box mt={2}>
                <Typography variant="h4" gutterBottom>
                  {t("settings.billing.planDetails")}
                </Typography>
                <Typography variant="body1" color="textSecondary">
                  {billingData.subscription.planDetails.name} -{" "}
                  {t("settings.billing.baseSeats")}:{" "}
                  {billingData.subscription.planDetails.baseSeats}
                </Typography>
              </Box>
            )}
            {billingData?.subscription?.status === "trialing" &&
              billingData?.subscription?.startDate &&
              billingData?.subscription?.endDate &&
              (() => {
                const totalTrialDays = dayjs(
                  billingData.subscription.endDate
                ).diff(dayjs(billingData.subscription.startDate), "day");
                const daysLeft = dayjs(billingData.subscription.endDate).diff(
                  dayjs(),
                  "day"
                );
                // Reverse progress: percent of trial days remaining
                const progress =
                  totalTrialDays > 0
                    ? Math.round((daysLeft / totalTrialDays) * 100)
                    : 0;
                return (
                  <Box mt={2}>
                    <Typography variant="h4" gutterBottom>
                      {t("settings.billing.trialInformation")}
                    </Typography>
                    <Typography variant="body1" color="textSecondary">
                      {`${daysLeft} ${t("settings.billing.daysLeft")}`}
                    </Typography>
                    <Box mt={1}>
                      <LinearProgress variant="determinate" value={progress} />
                      <Typography variant="caption">
                        {`${t("settings.billing.trialProgress")}: ${progress}%`}
                      </Typography>
                    </Box>
                  </Box>
                );
              })()}
            {subscriptionType == "trial" && (
              <Box mt={2}>
                <Typography variant="h4" gutterBottom>
                  {t("settings.billing.trialInformation")}
                </Typography>
                <Typography variant="body1" color="textSecondary"></Typography>
              </Box>
            )}
            {nextBilledAt ? (
              <Box mt={2}>
                <Typography variant="h4" gutterBottom>
                  {t("settings.billing.nextPaymentDate")}
                </Typography>
                <Typography variant="body1" color="textSecondary">
                  {dayjs(billingData.paddleProducts[0].nextBilledAt).format(
                    "DD MMM YYYY"
                  )}
                </Typography>
              </Box>
            ) : (
              subscriptionType !== "trial" && (
                <Box mt={2}>
                  <Typography variant="h4" gutterBottom>
                    {t("settings.billing.nextPaymentDate")}
                  </Typography>
                  <Typography variant="body1" color="textSecondary">
                    {t("settings.billing.noNextBillingDate")}
                  </Typography>
                </Box>
              )
            )}
            {currentBillingPeriod && (
              <Box mt={2}>
                <Typography variant="h4" gutterBottom>
                  {t("settings.billing.currentBillingPeriod")}
                </Typography>
                <Typography variant="body1" color="textSecondary">
                  {dayjs(currentBillingPeriod.startsAt).format("DD MMM YYYY")} -{" "}
                  {dayjs(currentBillingPeriod.endsAt).format("DD MMM YYYY")}
                </Typography>
              </Box>
            )}
          </CardContent>
          <CardActions>
            {paddleCustomerPortalUrl && (
              <Button
                variant="contained"
                color="primary"
                href={paddleCustomerPortalUrl}
                target="_blank"
                startIcon={<ManageAccounts />}
              >
                {t("settings.billing.manageSubscription")}
              </Button>
            )}
          </CardActions>
        </Card>
      </>
    );
  };

  return (
    <>
      <Box className={"container mainContainer docsContainer "}>
        <Box
          sx={{
            minHeight: "100vh",
            background: "#fff", // updated to white background
            py: 4,
            px: { xs: 2, md: 4 },
          }}
        >
          <Container maxWidth="lg">
            <div className="headerPush"></div>
            <SettingsHeader title={t("settings.billing.title")} />
            <Grid container spacing={3}>
              <Grid item xs={12} md={12}>
                {pageContent()}
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Box>
    </>
  );
};
export default Billing;
